import {
  Box,
  Flex,
  IconButton,
  Progress,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { FileUploadProgress, UploadStatus } from "./types";

interface UploadProgressListProps {
  uploadProgress: FileUploadProgress[];
  onCancelUpload: (index: number) => void;
}

const UploadProgressList: React.FC<UploadProgressListProps> = ({
  uploadProgress,
  onCancelUpload,
}) => {
  const { t } = useTranslation("common");

  return (
    <VStack spacing={3} align="stretch" mb={4}>
      {uploadProgress.map((fileProgress, index) => (
        <Box
          key={index}
          p={3}
          borderRadius="md"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
        >
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {fileProgress?.file?.name ?? t("projects.unknownFile")}
            </Text>

            {fileProgress.status === UploadStatus.UPLOADING && (
              <IconButton
                aria-label={t("projects.cancelUpload")}
                icon={<CloseIcon />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                borderRadius="full"
                onClick={() => onCancelUpload(index)}
                _hover={{ bg: "red.100" }}
              />
            )}

            {fileProgress.status === UploadStatus.COMPLETED && (
              <Text fontSize="xs" color="green.500" fontWeight="medium">
                ✓
              </Text>
            )}

            {fileProgress.status === UploadStatus.ERROR && (
              <Text fontSize="xs" color="red.500" fontWeight="medium">
                ✗
              </Text>
            )}
          </Flex>

          {fileProgress.status === UploadStatus.UPLOADING && (
            <Progress
              value={fileProgress.progress}
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              bg="gray.200"
            />
          )}

          {fileProgress.status === UploadStatus.COMPLETED && (
            <Progress
              value={100}
              size="sm"
              colorScheme="green"
              borderRadius="full"
              bg="gray.200"
            />
          )}

          {fileProgress.status === UploadStatus.CANCELLED && (
            <Text fontSize="xs" color="red.500">
              {t("projects.uploadCancelled")}
            </Text>
          )}

          {fileProgress.status === UploadStatus.ERROR && (
            <Text fontSize="xs" color="red.500">
              {fileProgress.errorMessage || t("projects.uploadFailed")}
            </Text>
          )}
        </Box>
      ))}
    </VStack>
  );
};

export default UploadProgressList;
